import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-oauth-callback',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './oauth-callback.component.html',
  styleUrls: ['./oauth-callback.component.scss']
})
export class OAuthCallbackComponent implements OnInit, OnDestroy {
  isLoading: boolean = true;
  error: string = '';
  success: boolean = false;
  provider: string = '';
  private subscription: Subscription = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    // Obtener parámetros de la URL del callback
    const routeSub = this.route.queryParams.subscribe(params => {
      const code = params['code'];
      const state = params['state'];
      const error = params['error'];
      const errorDescription = params['error_description'];
      
      // Obtener provider de la ruta
      this.provider = this.route.snapshot.params['provider'] || 'OAuth';

      console.log('OAuth Callback received:', { code, state, error, errorDescription, provider: this.provider });

      // Manejar errores de OAuth
      if (error) {
        this.handleOAuthError(error, errorDescription);
        return;
      }

      // Procesar código de autorización exitoso
      if (code) {
        this.handleOAuthSuccess(code, state);
      } else {
        this.handleOAuthError('invalid_request', 'Código de autorización no recibido');
      }
    });

    this.subscription.add(routeSub);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private handleOAuthError(error: string, description?: string): void {
    this.isLoading = false;
    this.success = false;

    // Mapear errores comunes a mensajes user-friendly
    switch (error) {
      case 'access_denied':
        this.error = 'Has cancelado el proceso de autenticación. Puedes intentar de nuevo.';
        break;
      case 'invalid_request':
        this.error = 'Error en la solicitud de autenticación. Por favor intenta de nuevo.';
        break;
      case 'unauthorized_client':
        this.error = 'Error de configuración de la aplicación. Por favor contacta soporte.';
        break;
      case 'invalid_scope':
        this.error = 'Permisos no válidos solicitados. Por favor contacta soporte.';
        break;
      default:
        this.error = description || `Error de autenticación con ${this.provider}. Por favor intenta de nuevo.`;
    }

    console.error('OAuth Error:', error, description);

    // Redirigir después de 5 segundos
    setTimeout(() => {
      this.router.navigate(['/login']);
    }, 5000);
  }

  private handleOAuthSuccess(code: string, state?: string): void {
    console.log(`Processing ${this.provider} OAuth success with code:`, code.substring(0, 10) + '...');
    
    // El código ya fue procesado por el backend
    // Aquí solo necesitamos manejar la redirección del usuario
    
    // Verificar si el usuario está ahora autenticado
    const authCheckSub = this.authService.isAuthenticated$.subscribe(isAuthenticated => {
      if (isAuthenticated) {
        // Usuario autenticado exitosamente
        this.success = true;
        this.isLoading = false;
        this.error = '';
        
        console.log(`${this.provider} OAuth login successful, redirecting to dashboard`);
        
        // Redirigir al dashboard después de un breve delay
        setTimeout(() => {
          this.router.navigate(['/dashboard']);
        }, 2000);
      } else {
        // Si no está autenticado, verificar el token actual
        this.checkAuthenticationStatus();
      }
    });

    this.subscription.add(authCheckSub);
  }

  private checkAuthenticationStatus(): void {
    // Intentar verificar el token actual
    const currentUser = this.authService.getCurrentUser();
    if (currentUser) {
      this.success = true;
      this.isLoading = false;
      setTimeout(() => {
        this.router.navigate(['/dashboard']);
      }, 2000);
    } else {
      // Si no hay usuario actual, algo salió mal
      this.handleOAuthError(
        'authentication_failed', 
        `No se pudo completar la autenticación con ${this.provider}. Inténtalo de nuevo.`
      );
    }
  }

  /**
   * Reintentar el proceso de OAuth
   */
  retryOAuth(): void {
    this.router.navigate(['/login']);
  }

  /**
   * Ir al dashboard (para casos de éxito)
   */
  goToDashboard(): void {
    this.router.navigate(['/dashboard']);
  }

  /**
   * Obtener emoji del proveedor para mostrar
   */
  getProviderEmoji(): string {
    switch (this.provider.toLowerCase()) {
      case 'facebook':
        return '📘';
      case 'google':
        return '🔍';
      case 'github':
        return '🐙';
      case 'microsoft':
        return '🟦';
      default:
        return '🔑';
    }
  }
}